import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [status, setStatus] = useState("polling");
  const [order, setOrder] = useState(null);
  const attemptsRef = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setOrder(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) {
            clear();
            cleared.current = true;
          }
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= 8) {
          setStatus("timeout");
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch {
        attemptsRef.current += 1;
        if (attemptsRef.current >= 5) {
          setStatus("error");
        } else {
          timer = setTimeout(poll, 2000);
        }
      }
    };

    poll();
    return () => { if (timer) clearTimeout(timer); };
  }, [sessionId, clear]);

  return (
    <main className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="success-page">
      {status === "polling" && (
        <div className="py-20" data-testid="success-polling">
          <Loader2 className="mx-auto animate-spin" size={36} strokeWidth={1.2} />
          <p className="font-display text-2xl mt-8">Confirming your order…</p>
          <p className="opacity-60 mt-3 text-sm">Please do not close this window.</p>
        </div>
      )}
      {status === "paid" && (
        <div className="py-20 fade-up" data-testid="success-paid">
          <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center" style={{ background: "var(--ink)" }}>
            <Check size={28} strokeWidth={1.5} color="var(--bone)" />
          </div>
          <p className="tracking-overline text-[11px] uppercase opacity-60 mt-8">Thank You</p>
          <h1 className="font-display text-5xl sm:text-6xl mt-4">Your piece is on its way.</h1>
          <p className="opacity-80 mt-6 max-w-md mx-auto leading-relaxed">
            We will hand-finish, wrap and dispatch your order within seven business days. A confirmation has been sent.
          </p>
          {order && (
            <p className="mt-6 text-sm opacity-60">Order reference: <span className="font-bodoni">{order.order_id}</span></p>
          )}
          <div className="mt-10 flex gap-4 justify-center">
            <Link to="/shop" className="btn-outline">Continue Browsing</Link>
            <Link to="/account" className="btn-primary">View Your Orders</Link>
          </div>
        </div>
      )}
      {(status === "expired" || status === "timeout" || status === "error") && (
        <div className="py-20" data-testid={`success-${status}`}>
          <h1 className="font-display text-4xl">Payment status unclear.</h1>
          <p className="opacity-70 mt-5">
            {status === "expired" ? "Your checkout session expired." : "We could not confirm your payment in time."}
          </p>
          <Link to="/cart" className="btn-outline mt-10 inline-flex">Return to Bag</Link>
        </div>
      )}
    </main>
  );
}
